import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useTransform, useInView, useReducedMotion } from 'framer-motion';
import { WHATSAPP_URL } from '../config/contact';
import { trackEvent } from '../lib/analytics';
import AboutSection from '../components/AboutSection';
import TeamSection from '../components/TeamSection';
import { COMPLIANCE_SHORT, COMPLIANCE_FULL, FAIS_EDUCATION_NOTE } from '../config/compliance';
import { PRECOLLECTION_NOTICE } from '../config/legal';
import { STAT_TILES } from '../config/stats';
import InstagramFeed from '../components/InstagramFeed';
import Logo from '../components/Logo';
import Seo, { SITE_URL as SEO_SITE } from '../components/Seo';
import {
  BookOpen, Brain, Wallet, BarChart3, Sparkles, Activity, Target,
  ArrowRight, CheckCircle2, Phone, Mail, User, MessageSquare,
  Menu, X, Building2, Instagram, Globe, Star, Quote
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = '#0A1F44';
const DEEP = '#071535';
const CARD = '#122C5A';
const GOLD = '#C0975C';
const IVORY = '#F5F1E8';
const MUTE = '#9fb0cc';
const display = { fontFamily: "'Outfit', sans-serif" };

const IMG = {
  hero: '/brand/hero-brand.webp',
  charts: 'https://images.unsplash.com/photo-1660020619062-70b16c44bf0f?auto=format&fit=crop&w=900&q=68',
  capeTown: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=1400&q=60',
};

const IG_URL = 'https://www.instagram.com/precision_wealth';
const SITE_URL = 'https://www.precisionwealth.finance';

const features = [
  { icon: BookOpen, title: 'Financial Courses', desc: '67 structured courses on budgeting, investing, tax and more.', c: '#0E7C7B' },
  { icon: Brain, title: 'Interactive Quizzes', desc: 'Reinforce every concept with short quizzes.', c: '#C0975C' },
  { icon: Wallet, title: 'Cashflow Overview', desc: 'See exactly where your money goes each month.', c: '#3E4E8B' },
  { icon: BarChart3, title: 'Financial Modelling', desc: 'Watch small savings compound into real wealth.', c: '#C56B4E' },
  { icon: Sparkles, title: 'Precision Assistant', desc: 'An AI education companion, available anytime.', c: '#2E7D5B' },
  { icon: Target, title: 'Goal Planner', desc: 'Set clear goals and track your progress.', c: '#C0975C' },
  { icon: Activity, title: 'Fitness Score', desc: 'One score that grows as you learn.', c: '#3E4E8B' },
];

const steps = [
  { n: '01', title: 'Create your account', desc: 'A minute to set up. No card, no obligation.' },
  { n: '02', title: 'Learn at your pace', desc: 'Courses, quizzes and tools that make money simple.' },
  { n: '03', title: 'Speak to an adviser', desc: 'When you are ready for advice, a Precision Wealth professional is a click away.' },
];

const quizTopics = ['Budgeting', 'Investing', 'Tax', 'Retirement', 'Property', 'Debt', 'Insurance', 'Entrepreneurship', 'Banking', 'Mindset'];

// Counts up to a target number when scrolled into view. Keeps any prefix/suffix (e.g. 60+, R0).
function CountUp({ raw }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const m = /^(\D*)(\d+)(\D*)$/.exec(raw);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!m || !inView) return;
    const target = parseInt(m[2], 10);
    const duration = 1500;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <span ref={ref}>{raw}</span>;
  return <span ref={ref}>{m[1]}{n}{m[3]}</span>;
}

const testimonials = [
  {
    quote: 'The Precision Wealth courses taught me how provident funds actually work, so when I changed jobs I knew exactly what to ask. A consultation with a Precision Wealth adviser then confirmed the right move for my situation, and I preserved my savings with total confidence.',
    name: 'Thabo M.',
    role: 'Individual client, Johannesburg',
    initials: 'TM',
    verified: false,
  },
  {
    quote: 'Learning on the Precision Wealth app finally helped me understand my real insurance needs. Sitting down with a Precision Wealth adviser made sure my cover was structured correctly for my family, so now I pay for exactly what fits my life. Another happy Precision Wealth client.',
    name: 'Kim N.',
    role: 'Individual client, Cape Town',
    initials: 'KN',
    verified: false,
  },
  {
    quote: 'The lessons on Precision Wealth made my tax obligations make sense for the first time. A consultation with a Precision Wealth adviser confirmed I was handling everything correctly, and I now walk into filing season calm and in control of my business.',
    name: 'Willem P.',
    role: 'Business owner, Pretoria',
    initials: 'WP',
    verified: false,
  },
];

// Only render testimonials whose consent/status has been verified by an admin.
const visibleTestimonials = testimonials.filter((t) => t.verified);

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', segment: 'individual', message: '', marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const bandRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const heroYRaw = useTransform(scrollY, [0, 700], [0, 70]);
  const { scrollYProgress: bandProgress } = useScroll({ target: bandRef, offset: ['start end', 'end start'] });
  const bandYRaw = useTransform(bandProgress, [0, 1], [-50, 50]);
  const heroY = reduceMotion ? 0 : heroYRaw;
  const bandY = reduceMotion ? 0 : bandYRaw;

  useEffect(() => {
    if (reduceMotion) return;
    const sections = Array.from(document.querySelectorAll('section')).slice(1);
    const targets = [];
    sections.forEach((sec) => {
      Array.from(sec.children).forEach((child) => {
        if (child.getBoundingClientRect) targets.push(child);
      });
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.style.opacity = '1';
          e.target.style.transform = 'none';
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    targets.forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
      io.observe(el);
    });
    return () => io.disconnect();
  }, [reduceMotion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || form.name.trim().length < 2) { setError('Please enter your full name.'); return; }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address.'); return; }
    if (!form.phone || form.phone.replace(/\D/g, '').length < 6) { setError('Please enter a valid phone number.'); return; }
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), segment: form.segment, message: form.message?.trim() || '', marketing_consent: !!form.marketing_consent };
      await axios.post(`${API}/leads`, payload, { headers: { 'Content-Type': 'application/json' } });
      trackEvent('generate_lead', { segment: form.segment });
      setSubmitted(true);
    } catch (err) {
      console.error('Lead submit failed:', err?.response?.status, err?.response?.data, err?.message);
      const detail = err?.response?.data?.detail;
      let msg = 'Something went wrong. Please try again or email us directly.';
      if (Array.isArray(detail) && detail.length) msg = detail.map((d) => d.msg || 'Invalid field').join(', ');
      else if (typeof detail === 'string') msg = detail;
      setError(msg);
    } finally { setSubmitting(false); }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const goldBtn = { backgroundColor: GOLD, color: NAVY };
  const ghostBtn = { border: '1px solid rgba(245,241,232,0.35)', color: IVORY };

  return (
    <div style={{ backgroundColor: NAVY, color: IVORY }} className="min-h-screen overflow-x-hidden font-sans">
      <Seo
        title="Precision Wealth — Find the extraordinary."
        description="Financial education for South Africans — courses, quizzes and calculators, plus qualified advisers for personal financial planning, investments, insurance and estate planning."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FinancialService',
          name: 'Precision Wealth',
          url: `${SEO_SITE}/`,
          image: `${SEO_SITE}/brand/og-image.jpg`,
          description: 'A division of the Hereford Group offering financial education and qualified financial advisers (representatives of Liberty Group Limited, FSP 2409).',
          areaServed: 'ZA',
          email: 'info@precisionwealth.co.za',
          sameAs: ['https://www.instagram.com/precision_wealth', 'https://www.precisionwealth.finance'],
        }}
      />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center"><Logo variant="light" size="md" layout="horizontal" /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: IVORY }}>
            <button onClick={() => scrollTo('learn')} className="hover:text-[#C0975C] transition-colors">Platform</button>
            <button onClick={() => scrollTo('preview')} className="hover:text-[#C0975C] transition-colors">Courses</button>
            <button onClick={() => scrollTo('about')} className="hover:text-[#C0975C] transition-colors">About</button>
            {visibleTestimonials.length > 0 && (
              <button onClick={() => scrollTo('stories')} className="hover:text-[#C0975C] transition-colors">Stories</button>
            )}
            <button onClick={() => scrollTo('consult')} className="hover:text-[#C0975C] transition-colors">Advisers</button>
            <Link to="/auth" className="hover:text-[#C0975C] transition-colors">Sign In</Link>
            <Link to="/auth?mode=signup" className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:opacity-90" style={goldBtn}>Get Started</Link>
          </nav>
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} style={{ color: IVORY }}>{menuOpen ? <X /> : <Menu />}</button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-6 pb-6 flex flex-col gap-4" style={{ backgroundColor: NAVY, color: IVORY }}>
            <button className="text-left" onClick={() => scrollTo('learn')}>Platform</button>
            <button className="text-left" onClick={() => scrollTo('preview')}>Courses</button>
            <button className="text-left" onClick={() => scrollTo('about')}>About</button>
            <button className="text-left" onClick={() => scrollTo('consult')}>Advisers</button>
            <Link to="/auth" onClick={() => setMenuOpen(false)}>Sign In</Link>
            <Link to="/auth?mode=signup" onClick={() => setMenuOpen(false)} className="px-5 py-3 rounded-full text-sm font-semibold text-center" style={goldBtn}>Get Started</Link>
          </div>
        )}
      </header>

      <main id="main-content">
      {/* HERO */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden" style={{ background: `linear-gradient(160deg, ${CARD} 0%, ${NAVY} 55%, ${DEEP} 100%)` }}>
        <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(192,151,92,0.18)' }} />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-xs font-semibold tracking-[0.3em] uppercase mb-5" style={{ color: GOLD }}>Financial Education by Precision Wealth</div>
            <h1 style={{ color: IVORY, ...display }} className="text-5xl md:text-6xl xl:text-7xl font-extrabold leading-[1.02] mb-6 tracking-tight">
              Get money-smart.<br /><span className="text-gradient-gold">Find the extraordinary.</span>
            </h1>
            <p style={{ color: '#c6d0e0' }} className="text-lg leading-relaxed mb-8 max-w-md">
              Learn to budget, invest and build wealth, then talk to a Precision Wealth adviser when you are ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/auth?mode=signup" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-all hover:opacity-90" style={goldBtn}>Start Learning <ArrowRight className="w-4 h-4" /></Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-all hover:bg-white/5" style={ghostBtn}><MessageSquare className="w-4 h-4" /> Chat to an Adviser</a>
            </div>
            <div className="flex items-center gap-3 mt-8 pt-6" style={{ borderTop: '1px solid rgba(245,241,232,0.12)' }}>
              <Logo variant="light" size="sm" layout="horizontal" />
              <span className="text-xs" style={{ color: MUTE }}>{COMPLIANCE_SHORT}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden h-[520px]" style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.45)' }}>
              <motion.img src={IMG.hero} alt="Precision Wealth — building wealth with precision" width={848} height={1264} style={{ y: heroY }} className="absolute inset-x-0 -top-[45px] w-full h-[620px] object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(10,31,68,0.5) 100%)' }} />
            </div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="absolute -right-5 bottom-16 bg-white rounded-xl p-5 shadow-xl w-52">
              <div className="flex items-center justify-between mb-2"><span className="text-xs text-gray-500">Financial Fitness Score</span><span style={{ ...display, color: GOLD }} className="font-bold text-lg">72</span></div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: '72%', backgroundColor: GOLD }} /></div>
              <div className="text-[11px] text-gray-400 mt-2">Grows as you learn</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ backgroundColor: DEEP }}>
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STAT_TILES.map(([k, v]) => (
            <div key={v}><div style={{ color: GOLD, ...display }} className="text-4xl md:text-5xl font-extrabold"><CountUp raw={k} /></div><div style={{ color: MUTE }} className="text-sm mt-1">{v}</div></div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="learn" className="relative overflow-hidden py-24">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="absolute top-8 right-0 w-[440px] h-[440px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(192,151,92,0.10), transparent 70%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">The Platform</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-4">Practical tools for real financial progress.</h2>
            <p className="text-[15px] leading-relaxed" style={{ color: MUTE }}>Everything you need to take control of your money, built on the principles our advisers use every day.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                className="p-7 rounded-2xl transition-all hover:-translate-y-1.5 shadow-xl shadow-black/20 hover:border-[#C0975C]/50" style={{ backgroundColor: CARD, border: '1px solid #1D3D72' }}>
                <div className="w-[52px] h-[52px] rounded-xl flex items-center justify-center mb-5 bg-[#C0975C]/15 border border-[#C0975C]/30"><s.icon className="w-6 h-6" style={{ color: GOLD }} /></div>
                <h3 style={{ ...display, color: IVORY }} className="text-lg font-bold mb-2">{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: MUTE }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSE & QUIZ PREVIEW (light app screenshots on dark bg) */}
      <section id="preview" className="py-24" style={{ backgroundColor: DEEP }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">A Look Inside</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold">See the learning journey.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { src: '/brand/shots/shot-dashboard.webp', label: 'Your Dashboard' },
              { src: '/brand/shots/shot-modelling.webp', label: 'Financial Modelling' },
              { src: '/brand/shots/shot-courses.webp', label: 'Courses' },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{ border: '1px solid #1D3D72', backgroundColor: '#061530' }}
              >
                <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: '#0A1F44', borderBottom: '1px solid #1D3D72' }}>
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3a4a6b' }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3a4a6b' }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GOLD }} />
                  </div>
                  <span className="text-xs font-semibold ml-1" style={{ color: IVORY, ...display }}>{s.label}</span>
                </div>
                <img src={s.src} alt={`Precision Wealth ${s.label}`} width={1100} height={733} className="w-full h-auto block" loading="lazy" />
              </motion.div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-2.5 mt-10">
            {quizTopics.map((t) => (<span key={t} className="px-4 py-1.5 rounded-full text-sm font-medium" style={{ color: IVORY, border: '1px solid rgba(192,151,92,0.3)' }}>{t}</span>))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative overflow-hidden py-24">
        <div className="absolute bottom-0 left-[-80px] w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(29,61,114,0.4), transparent 70%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative"><img src={IMG.charts} alt="Financial planning and market analysis" loading="lazy" className="relative w-full h-[460px] object-cover rounded-2xl" /></div>
          <div>
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">How It Works</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-8">From learning to real advice.</h2>
            <div className="space-y-6">
              {steps.map((s) => (
                <div key={s.n} className="flex gap-5">
                  <div className="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center font-bold" style={{ backgroundColor: CARD, color: GOLD, border: '1px solid rgba(192,151,92,0.3)', ...display }}>{s.n}</div>
                  <div><h3 style={{ ...display, color: IVORY }} className="text-xl font-bold mb-1">{s.title}</h3><p className="text-sm leading-relaxed" style={{ color: MUTE }}>{s.desc}</p></div>
                </div>
              ))}
            </div>
            <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full font-semibold transition-all hover:opacity-90" style={goldBtn}>Create Your Account <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* ABOUT PRECISION WEALTH */}
      <AboutSection id="about" />
      <TeamSection id="team" />

      <section id="about-services" className="py-24" style={{ backgroundColor: DEEP }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">What we do</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-6">Advice, education and service.</h2>
            <p className="text-[15px] leading-relaxed mb-8" style={{ color: '#c6d0e0' }}>
              A dynamic financial services company, and a division of the Hereford Group. We do not just advise, we listen. This platform is our commitment to giving all South Africans the knowledge to build wealth with confidence.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all hover:opacity-90" style={goldBtn}><Globe className="w-4 h-4" /> precisionwealth.finance</a>
              <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all hover:bg-white/5" style={ghostBtn}><Instagram className="w-4 h-4" /> @precision_wealth</a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[['Personal Financial Planning', 'Protecting and growing your wealth.'], ['Investment Advice', 'Local and offshore strategies.'], ['Short & Long-term Insurance', 'Life, disability and asset cover.'], ['Corporate Benefits', 'Employee benefits and group schemes.'], ['Private Client Structuring', 'Bespoke structuring for private clients.'], ['Wills & Estates', 'Estate planning and trusts.'], ['Healthcare', 'Integrated medical scheme cover.']].map(([t, d]) => (
              <div key={t} className="p-5 rounded-2xl" style={{ backgroundColor: CARD, border: '1px solid rgba(192,151,92,0.2)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: NAVY }}><CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} /></div>
                <h4 style={{ ...display, color: IVORY }} className="font-bold mb-1 text-sm">{t}</h4>
                <p className="text-xs leading-relaxed" style={{ color: MUTE }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {visibleTestimonials.length > 0 && (
      <section id="stories" className="relative overflow-hidden py-24">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 right-[-80px] w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(192,151,92,0.10), transparent 70%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">Client Stories</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-4">Real decisions, made with confidence.</h2>
            <p className="text-[15px] leading-relaxed" style={{ color: MUTE }}>A few of the moments where the right guidance made all the difference for South Africans like you.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {visibleTestimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative p-7 rounded-2xl shadow-xl shadow-black/20"
                style={{ backgroundColor: CARD, border: '1px solid #1D3D72' }}
              >
                <Quote className="w-8 h-8 mb-4" style={{ color: GOLD, opacity: 0.5 }} />
                <div className="flex gap-1 mb-4">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4" style={{ color: GOLD, fill: GOLD }} />
                  ))}
                </div>
                <p className="text-[15px] leading-relaxed mb-6" style={{ color: '#dfe6f0' }}>{t.quote}</p>
                <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid rgba(192,151,92,0.15)' }}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" style={{ backgroundColor: GOLD, color: NAVY, ...display }}>{t.initials}</div>
                  <div>
                    <div className="font-bold text-sm" style={{ ...display, color: IVORY }}>{t.name}</div>
                    <div className="text-xs" style={{ color: MUTE }}>{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SOCIAL PROOF BAND */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" style={{ backgroundColor: GOLD }}>
            <div>
              <h2 style={{ ...display, color: NAVY }} className="text-3xl md:text-4xl font-bold mb-4">The earlier you start, the further you go.</h2>
              <p className="text-[15px] leading-relaxed" style={{ color: '#3a3320' }}>Small money habits today become serious wealth tomorrow. Precision Wealth gives all South Africans a head start.</p>
              <div className="flex flex-wrap gap-3 mt-6">{['Budgeting', 'Investing', 'Tax', 'Business', 'Saving'].map((t) => (<span key={t} className="px-4 py-1.5 rounded-full text-sm font-semibold" style={{ backgroundColor: NAVY, color: IVORY }}>{t}</span>))}</div>
            </div>
            <img src="/brand/office/office-2.webp" alt="Precision Wealth office" loading="lazy" className="w-full h-72 object-cover rounded-xl" />
          </div>
        </div>
      </section>

      {/* CONSULTATION FORM */}
      <section id="consult" className="relative overflow-hidden py-24">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="absolute top-1/4 right-[-100px] w-[460px] h-[460px] rounded-full blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(192,151,92,0.10), transparent 70%)' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">Speak to an Adviser</div>
            <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-6 leading-tight">Ready for advice tailored to you?</h2>
            <p className="text-[15px] leading-relaxed mb-8" style={{ color: MUTE }}>
              The platform teaches you the concepts and gives you tips. When your questions become personal, our Assistant cannot advise you under FAIS, so chat to a qualified Precision Wealth adviser on our WhatsApp Hotline, or leave your details and an adviser will reach out for a confidential, no-pressure conversation.
            </p>
            <div className="space-y-4">
              {['No cost, no obligation', 'For individuals and businesses', 'Qualified, professional advisers'].map((b) => (
                <div key={b} className="flex items-center gap-3" style={{ color: IVORY }}><div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: GOLD }}><CheckCircle2 className="w-4 h-4" style={{ color: NAVY }} /></div><span className="text-sm font-medium">{b}</span></div>
              ))}
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="consult-whatsapp-cta" className="inline-flex items-center gap-2 mt-8 px-6 py-3.5 rounded-full font-semibold transition-all hover:opacity-90" style={{ backgroundColor: GOLD, color: NAVY }}><MessageSquare className="w-4 h-4" /> Chat to an adviser on WhatsApp now</a>
            <img src={IMG.charts} alt="" aria-hidden="true" loading="lazy" className="w-full h-56 object-cover rounded-2xl mt-10 hidden lg:block" />
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: GOLD }}><CheckCircle2 className="w-9 h-9" style={{ color: NAVY }} /></div>
                <h3 style={{ ...display, color: NAVY }} className="text-2xl font-bold mb-3">Thank you.</h3>
                <p style={{ color: '#4a5568' }} className="text-sm leading-relaxed">A Precision Wealth adviser will be in touch shortly. In the meantime, why not <Link to="/auth" style={{ color: NAVY }} className="font-semibold">start learning</Link>?</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-2 font-semibold" style={{ color: NAVY }}>Full Name</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-50" style={{ border: '1px solid rgba(10,31,68,0.12)' }}><User className="w-4 h-4" style={{ color: GOLD }} /><input name="name" value={form.name} onChange={handleChange} placeholder="Your name" className="w-full outline-none text-sm bg-transparent" style={{ color: NAVY }} /></div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-2 font-semibold" style={{ color: NAVY }}>Email</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-50" style={{ border: '1px solid rgba(10,31,68,0.12)' }}><Mail className="w-4 h-4" style={{ color: GOLD }} /><input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@email.com" className="w-full outline-none text-sm bg-transparent" style={{ color: NAVY }} /></div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-2 font-semibold" style={{ color: NAVY }}>Phone</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-50" style={{ border: '1px solid rgba(10,31,68,0.12)' }}><Phone className="w-4 h-4" style={{ color: GOLD }} /><input name="phone" value={form.phone} onChange={handleChange} placeholder="+27 ..." className="w-full outline-none text-sm bg-transparent" style={{ color: NAVY }} /></div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-2 font-semibold" style={{ color: NAVY }}>I am</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ v: 'individual', l: 'An Individual', icon: User }, { v: 'corporate', l: 'A Business', icon: Building2 }].map((opt) => (
                      <button type="button" key={opt.v} onClick={() => setForm({ ...form, segment: opt.v })} className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all" style={form.segment === opt.v ? { backgroundColor: NAVY, color: IVORY } : { border: '1px solid rgba(10,31,68,0.15)', color: NAVY }}><opt.icon className="w-4 h-4" /> {opt.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-2 font-semibold" style={{ color: NAVY }}>Message <span className="normal-case text-gray-400">(optional)</span></label>
                  <div className="flex gap-2 px-4 py-3 rounded-xl bg-gray-50" style={{ border: '1px solid rgba(10,31,68,0.12)' }}><MessageSquare className="w-4 h-4 mt-0.5" style={{ color: GOLD }} /><textarea name="message" value={form.message} onChange={handleChange} rows={3} placeholder="What is on your mind?" className="w-full outline-none text-sm bg-transparent resize-none" style={{ color: NAVY }} /></div>
                </div>
                {error && <p className="text-sm" style={{ color: '#b91c1c' }}>{error}</p>}
                <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: NAVY }} data-testid="lead-marketing-consent-label">
                  <input type="checkbox" name="marketing_consent" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5" data-testid="lead-marketing-consent" />
                  <span>Yes, I would like to receive occasional financial tips and updates from Precision Wealth. (Optional)</span>
                </label>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(10,31,68,0.6)' }}>
                  {PRECOLLECTION_NOTICE} See our <Link to="/privacy" className="underline" style={{ color: NAVY }}>Privacy Notice</Link>.
                </p>
                <button type="submit" disabled={submitting} className="w-full py-4 rounded-full font-semibold transition-all hover:opacity-90 disabled:opacity-60" style={goldBtn}>{submitting ? 'Sending…' : 'Request a Consultation'}</button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SOUTH AFRICA BAND */}
      <section ref={bandRef} className="relative py-28 overflow-hidden" style={{ backgroundColor: DEEP }}>
        <motion.img src={IMG.capeTown} alt="" aria-hidden="true" loading="lazy" style={{ y: bandY }} className="absolute inset-0 w-full h-full object-cover opacity-30 scale-125" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(7,21,53,0.55), rgba(7,21,53,0.88))' }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-4">Proudly South African</div>
          <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold mb-4">Built for South Africa, from the ground up.</h2>
          <p className="text-[15px] max-w-2xl mx-auto" style={{ color: '#c6d0e0' }}>SARS tax tables, the JSE, tax-free savings, the two-pot system and more, all explained in a local context you can actually use.</p>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-24" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div style={{ color: GOLD }} className="text-xs font-semibold tracking-[0.3em] uppercase mb-3">On Instagram</div>
              <h2 style={{ ...display, color: IVORY }} className="text-4xl md:text-5xl font-bold">Follow @precision_wealth</h2>
            </div>
            <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold self-start" style={goldBtn}><Instagram className="w-4 h-4" /> Follow us</a>
          </div>
          <InstagramFeed limit={8} />
          <p className="text-center text-xs mt-8" style={{ color: MUTE }}>Our latest posts, straight from @precision_wealth on Instagram.</p>
        </div>
      </section>

      </main>

      {/* FOOTER */}
      <footer style={{ backgroundColor: DEEP, color: IVORY }} className="pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-10 mb-12">
            <div>
              <div className="mb-4"><Logo variant="light" size="md" layout="horizontal" /></div>
              <p className="text-sm mb-4" style={{ color: MUTE }}>Find The Extraordinary.</p>
              <div className="flex gap-3">
                <a href={IG_URL} target="_blank" rel="noopener noreferrer" aria-label="Precision Wealth on Instagram" className="w-9 h-9 rounded-full flex items-center justify-center" style={{ border: '1px solid rgba(192,151,92,0.4)' }}><Instagram className="w-4 h-4" style={{ color: GOLD }} /></a>
                <a href={SITE_URL} target="_blank" rel="noopener noreferrer" aria-label="Precision Wealth website" className="w-9 h-9 rounded-full flex items-center justify-center" style={{ border: '1px solid rgba(192,151,92,0.4)' }}><Globe className="w-4 h-4" style={{ color: GOLD }} /></a>
              </div>
            </div>
            <div>
              <div style={{ color: GOLD }} className="text-xs tracking-[0.2em] uppercase mb-4 font-semibold">Explore</div>
              <ul className="space-y-2 text-sm" style={{ color: MUTE }}>
                <li><button onClick={() => scrollTo('learn')} className="hover:text-white">Platform</button></li>
                <li><button onClick={() => scrollTo('preview')} className="hover:text-white">Courses & Quizzes</button></li>
                <li><button onClick={() => scrollTo('about')} className="hover:text-white">About</button></li>
                <li><Link to="/auth" className="hover:text-white">Sign In</Link></li>
                <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link></li>
                <li><Link to="/disclosures" className="hover:text-white" data-testid="footer-disclosures">Disclosures</Link></li>
              </ul>
            </div>
            <div>
              <div style={{ color: GOLD }} className="text-xs tracking-[0.2em] uppercase mb-4 font-semibold">Contact</div>
              <ul className="space-y-2 text-sm" style={{ color: MUTE }}>
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" style={{ color: GOLD }} /> info@precisionwealth.co.za</li>
                <li className="flex items-center gap-2"><Globe className="w-4 h-4" style={{ color: GOLD }} /> www.precisionwealth.finance</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 text-xs leading-relaxed" style={{ borderTop: '1px solid rgba(192,151,92,0.2)', color: '#9fb0cc' }}>
            <p className="mb-3">{COMPLIANCE_FULL} {FAIS_EDUCATION_NOTE}</p>
            <p>© {new Date().getFullYear()} Precision Wealth. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
